import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { AffiliateModule, ApplicationModule, AuthModule } from '..'
import { AFFILIATES, URLS } from '../../factories'
import { CookieHelper, RegExHelper, RequestHelper, StringHelper } from '../../helpers'
import { CloudinaryService } from '../../services'

import FullAddress from '../../types/address/fullAddress'
import AddressDto from '../../types/dto/addressDto'
import ApplicationFormDto from '../../types/dto/applicationFormDto'
import EmploymentDto from '../../types/dto/employmentDto'
import LookupValueDto from '../../types/dto/lookupValueDto'
import AffiliateConfig from '@/types/affiliate/affiliate-config'
import axios from 'axios'
import Vehicle from '../../types/application/Vehicle'
import Utm from '../../types/application/Utm'
import VehicleType from "@/const/vehicle_type";
import DefaultValue from "@/const/default_value";

export interface IState {
	applicationID: string | null;
}

@Module(
  {
    name: 'Application'
  }
)
export default class Application extends VuexModule {
  // #region Form

	form = {
	  title: {
	    id: 1,
	    name: 'Mr'
	  } as LookupValueDto,
	  vehicleType: {
	    id: VehicleType.Car // default to car
	  } as LookupValueDto,
	  maritalStatus: {
	    id: 24,
	    description: '',
	    enumerator: 0,
	    name: ''
	  } as LookupValueDto,
	  drivingLicence: {
	    id: 35,
	    description: '',
	    enumerator: 0,
	    name: ''
	  } as LookupValueDto,
	  firstName: '',
	  lastName: '',
	  email: '',
	  contactNumber: '',
	  loanAmount: DefaultValue.LoanAmount,
	  netMonthlyIncome: null,
	  dateOfBirth: null,
	  employments: [] as EmploymentDto[],
	  addresses: [] as AddressDto[],
		submissionType: null,
	} as ApplicationFormDto;

	formCopy = {
	  title: {
	    id: 1,
	    name: 'Mr'
	  } as LookupValueDto,
	  vehicleType: {
	    id: 40 // default to car
	  } as LookupValueDto,
	  maritalStatus: {
	    id: 24,
	    description: '',
	    enumerator: 0,
	    name: ''
	  } as LookupValueDto,
	  drivingLicence: {
	    id: 35,
	    description: '',
	    enumerator: 0,
	    name: ''
	  } as LookupValueDto,
	  firstName: '',
	  lastName: '',
	  email: '',
	  contactNumber: '',
	  loanAmount: DefaultValue.LoanAmount,
	  netMonthlyIncome: null,
	  dateOfBirth: null,
	  employments: [] as EmploymentDto[],
	  addresses: [] as AddressDto[],
		submissionType: null,
	} as ApplicationFormDto;

	@Mutation
	private setForm (val: ApplicationFormDto) {
	  this.form = {} as ApplicationFormDto
	  this.form = val
	}

	@Action
	updateForm (val: ApplicationFormDto) {
	  this.setForm(val)
	}

	@Mutation
	private setFormCopy (val: ApplicationFormDto) {
	  this.formCopy = {} as ApplicationFormDto
	  this.formCopy = val
	}

	@Action
	updateFormCopy (val: ApplicationFormDto) {
	  this.setFormCopy(val)
	}
	// #endregion

	@Mutation
	private insertAddress (val: AddressDto) {
	  this.form.addresses.push(val)
	}

	@Action
	addAddress (val: AddressDto) {
	  this.insertAddress(val)
	}

	@Mutation
	private setEditAddress (val: AddressDto) {
	  const index = this.form.addresses.findIndex(e => e.addressLevel === val.addressLevel)
	  index > -1 ? this.form.addresses[index] = val : null
	}

	@Action
	editAddress (val: AddressDto) {
	  this.setEditAddress(val)
	}

	@Mutation
	private insertEmployment (val: EmploymentDto) {
	  this.form.employments.push(val)
	}

	@Action
	addEmployment (val: EmploymentDto) {
	  this.insertEmployment(val)
	}

	@Mutation
	private setEditEmployment (val: EmploymentDto) {
	  const index = this.form.employments.findIndex(e => e.employmentLevel === val.employmentLevel)
	  index > -1 ? this.form.employments[index] = val : null
	}

	@Action
	editEmployment (val: EmploymentDto) {
	  this.setEditEmployment(val)
	}

	// #region URL Referrer

	urlReferrer = '';

	@Mutation
	private setUrlReferrer (val: string) {
	  this.urlReferrer = val
	}

	@Action
	updateUrlReferrer () {
	  this.setUrlReferrer(AuthModule.isProduction ? location.host : 'oceanfinance.carfinance247.co.uk')
	}

	// #endregion

	// #region isLoaded

	isLoaded = false;

	@Mutation
	private setIsLoaded (val: boolean) {
	  this.isLoaded = val
	}

	@Action
	updateIsLoaded (val: boolean) {
	  this.setIsLoaded(val)
	}

	// #endregion

	// #region Full App Success

	isPrePopped = false;

	@Mutation
	private setIsPrePopped (val: boolean) {
	  this.isPrePopped = val
	}

	@Action
	updateIsPrePopped (val: boolean) {
	  this.setIsPrePopped(val)
	}

	// #endregion

	// #region Full App Success

	agreedToTerms = false;

	@Mutation
	private setAgreedToTerms (val: boolean) {
	  this.agreedToTerms = val
	}

	@Action
	updateAgreedToTerms (val: boolean) {
	  this.setAgreedToTerms(val)
	}

	// #endregion

	// #region Applied Date

	appliedDate: Date | null = null;

	@Mutation
	private setAppliedDate (val: Date) {
	  this.appliedDate = val
	}

	@Action
	updateAppliedDate () {
	  this.setAppliedDate(new Date())
	}

	// #endregion

	// #region GUID

	GUID = '';

	@Mutation
	private setGUID (val: string) {
	  this.GUID = val
	}

	@Action
	updateGUID () {
	  if (this.GUID !== '') { return }

	  let e = new Date().getTime()
	  e += performance.now()

	  const id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (t) {
	    const i = (e + 16 * Math.random()) % 16 | 0
	    return e = Math.floor(e / 16),
	    (t === 'x' ? i : 3 & i | 8).toString(16)
	  })

	  this.setGUID(id)
	}

	// #endregion

	// #region Show Loan Amount

	showLoanAmount = false;

	@Mutation
	private setShowLoanAmount (val: boolean) {
	  this.showLoanAmount = val
	}

	@Action
	updateShowLoanAmount (val: boolean) {
	  this.setShowLoanAmount(val)
	}

	// #endregion

	// #region Show Loan Amount

	isNewAddress = true;

	@Mutation
	private setIsNewAddress (val: boolean) {
	  this.isNewAddress = val
	}

	@Action
	updateIsNewAddress (val: boolean) {
	  this.setIsNewAddress(val)
	}

	// #endregion

	// #region Show Loan Amount

	isNewEmployment = true;

	@Mutation
	private setIsNewEmployment (val: boolean) {
	  this.isNewEmployment = val
	}

	@Action
	updateIsNewEmployment (val: boolean) {
	  this.setIsNewEmployment(val)
	}

	// #endregion

	// #region IsMonthlyBudgetAllowed

	isMonthlyBudgetAllowed = false;

	@Mutation
	private setIsMonthlyBudgetAllowed (val: boolean) {
	  this.isMonthlyBudgetAllowed = val
	}

	@Action
	updateIsMonthlyBudgetAllowed (val: boolean) {
	  this.setIsMonthlyBudgetAllowed(val)
	}

	// #endregion

	// #region Loan Amount

	loanAmount = DefaultValue.LoanAmount;

	@Mutation
	private setLoanAmount (val: number) {
	  this.loanAmount = val
	}

	@Action
	updateLoanAmount () {
	  const loanCookie = CookieHelper.get('loanCookie')

	  let amount = DefaultValue.LoanAmount

	  if (loanCookie) {
	    const cookieParts = loanCookie.split('&')
	    const queryString: any = {}

	    for (let i = 0; i < cookieParts.length; i++) {
	      const pair = cookieParts[i].split('-')

	      queryString[pair[0]] = decodeURIComponent(pair[1])

	      amount = parseInt(queryString.LoanAmount)
	    }
	  }

	  amount = RequestHelper.getQueryVariable('amount') && !isNaN(parseInt(RequestHelper.getQueryVariable('amount')!)) ? parseInt(RequestHelper.getQueryVariable('amount') ?? '') : amount
	  amount = RequestHelper.getQueryVariable('amount_uswitch') && !isNaN(parseInt(RequestHelper.getQueryVariable('amount_uswitch') ?? '')) ? parseInt(RequestHelper.getQueryVariable('amount_uswitch') ?? '') : amount
	  amount = RequestHelper.getQueryVariable('loan-amount-prepop') && !isNaN(parseInt(RequestHelper.getQueryVariable('loan-amount-prepop') ?? '')) ? parseInt(RequestHelper.getQueryVariable('loan-amount-prepop') ?? '') : amount

	  const show = RequestHelper.getQueryVariable('loan-amount-prepop') == null ||
			isNaN(parseInt(RequestHelper.getQueryVariable('loan-amount-prepop') ?? ''))

	  this.updateShowLoanAmount(show)

	  if (this.isMonthlyBudgetAllowed) {
	    amount = RequestHelper.getQueryVariable('monthlyBudget') && !isNaN(parseInt(RequestHelper.getQueryVariable('monthlyBudget')!)) ? Math.round(parseInt(RequestHelper.getQueryVariable('monthlyBudget')!) * 60) : amount
	  }

	  this.setLoanAmount(amount)
	}

	@Mutation
	private setVehicleTypeId (val: number) {
		this.form.vehicleType.id = val
	}

	@Action
	updateVehicleTypeId () {
		const vehicle_type = RequestHelper.getQueryVariable('vehicle_type')

		if (vehicle_type) {
			const index = Object.keys(VehicleType).findIndex(v => v.toUpperCase() === vehicle_type.toUpperCase().trim());
			const vehicleTypeId = index > -1 ? Object.values(VehicleType)[index] : VehicleType.Car;

			this.setVehicleTypeId(vehicleTypeId);
		}
	}

	// #endregion

	// #region VehicleID

	vehicleID: string | null = null;

	@Mutation
	private setVehicleID (val: string | null) {
	  this.vehicleID = val
	}

	@Action
	updateVehicleID (val: string | null) {
	  this.setVehicleID(val)
	}

	// #endregion

	// #region Vehicle

	vehicle: Vehicle | null = null;

	@Mutation
	private setVehicle (val: Vehicle) {
	  this.vehicle = val
	}

	@Action
	updateVehicle (val: Vehicle) {
	  this.setVehicle(val)
	}

	@Action
	updateVehicleOnLoad () {
	  const obj: Vehicle = {} as Vehicle

	  obj.vrm = RequestHelper.getQueryVariable('vrm') ?? ''
	  if (obj.vrm) {
	    obj.vrm = obj.vrm.toUpperCase()
	  }
	  obj.registrationYear = RequestHelper.getQueryVariable('year') ?? undefined
	  obj.make = RequestHelper.getQueryVariable('make') ?? undefined
	  obj.model = RequestHelper.getQueryVariable('model') ?? undefined
	  obj.price = RequestHelper.getQueryVariable('price') ?? undefined
	  obj.mileage = RequestHelper.getQueryVariable('mileage') ?? undefined
	  obj.fuelType = RequestHelper.getQueryVariable('fuel') ?? undefined
	  obj.transmission = RequestHelper.getQueryVariable('transmission') ?? undefined
	  obj.colour = RequestHelper.getQueryVariable('colour') ?? undefined
	  obj.imageUrl = RequestHelper.getQueryVariable('imageurl') ?? undefined
	  obj.vdpUrl = RequestHelper.getQueryVariable('vdpurl') ?? undefined
	  obj.dealerInfoUrl = RequestHelper.getQueryVariable('dealerinfourl') ?? undefined

	  if (obj.imageUrl && obj.imageUrl.indexOf('cloudinary') === -1) {
	    const cloudinaryVehicleImageUrl = CloudinaryService.getCloudinaryImageUrl(obj.imageUrl)

	    this.updateCloudinaryVehicleImageUrl(cloudinaryVehicleImageUrl)
	  }

	  this.setVehicle(obj)

	  this.setVehicleID(RequestHelper.getQueryVariable('vehicleid') ? RequestHelper.getQueryVariable('vehicleid') : null)
	}

	// #endregion

	// #region cloudinaryVehicleImageUrl

	cloudinaryVehicleImageUrl: string | null = null;

	@Mutation
	private setCloudinaryVehicleImageUrl (val: string) {
	  this.cloudinaryVehicleImageUrl = val
	}

	@Action
	updateCloudinaryVehicleImageUrl (val: string) {
	  this.setCloudinaryVehicleImageUrl(val)
	}

	// #endregion

	// #region UTM Parameters

	utmParameters: Utm | null = null;

	@Mutation
	private setUTMParameters (val: Utm) {
	  this.utmParameters = val
	}

	@Action
	updateUTMParameters () {
	  const obj = {} as Utm

	  let affiliateUserId = RequestHelper.getQueryVariable('aid')

	  if (!affiliateUserId) {
	    if (AffiliateModule.affiliateConfig && AffiliateModule.affiliateConfig.affiliateUserId) {
	      affiliateUserId = AffiliateModule.affiliateConfig.affiliateUserId.toString()
	    } else {
	      const host = AffiliateModule.host

	      let affiliateConfig = {} as AffiliateConfig

	      if (Object.keys(AFFILIATES).includes(host)) {
	        // Get config from factory
	        affiliateConfig = AFFILIATES[host as keyof typeof AFFILIATES] as AffiliateConfig
	      }

	      if (affiliateConfig && affiliateConfig.affiliateUserId) {
	        affiliateUserId = affiliateConfig.affiliateUserId.toString()
	      }
	    }
	  }

	  if (document.cookie.split(';').filter((item) => item.trim().startsWith('cf247-utm=')).length) {
	    const utmCookie = CookieHelper.get('cf247-utm')
	    const cookie = JSON.parse(utmCookie)

	    obj.source = cookie.UtmSource
	    obj.medium = cookie.UtmMedium
	    obj.term = cookie.UtmTerm
	    obj.content = cookie.UtmContent
	    obj.campaign = cookie.UtmCampaign
	    obj.type = cookie.UtmType
	    obj.sid = ApplicationModule.form && ApplicationModule.form.utmParameters && ApplicationModule.form.utmParameters.sid ? ApplicationModule.form.utmParameters.sid : undefined
	    obj.mkwId = cookie.Mkwid
	    obj.gclid = cookie.Gclid
	    obj.agentPublicId = ApplicationModule.form && ApplicationModule.form.utmParameters && ApplicationModule.form.utmParameters.agentPublicId ? ApplicationModule.form.utmParameters.agentPublicId : undefined
	    obj.cid = obj.mkwId && RegExHelper.extract(obj.mkwId, /campaignid\|(\d+)/i) !== null ? Number(RegExHelper.extract(obj.mkwId, /campaignid\|(\d+)/i)) : undefined
	    obj.referrerApplicationId = cookie.Rai

	    if (cookie.Aid) { affiliateUserId = cookie.Aid }

	    if (typeof (window as any).ga !== 'undefined' && (window as any).ga !== null) {
	      (window as any).ga((tracker: any) => {
	        const googleClientId = tracker.get('clientId')
	        if (googleClientId || obj.gclid) {
	          const attributionUrl = AuthModule.isProduction ? URLS.production.attributionApiUrl : URLS.replica.attributionApiUrl

	          const tokenString = 'Bearer ' + AuthModule.token!.authenticationToken
	          const headersObj = { Authorization: tokenString }

	          axios({
	            method: 'POST',
	            url: attributionUrl + '/api/Attribution',
	            headers: headersObj,
	            data: {
	              guid: ApplicationModule.GUID,
	              googleClientId: googleClientId,
	              gclid: obj.gclid
	            }
	          }).catch(error => {
	            window.console.error(error.message)
	          })
	        }
	      })
	    }

	    this.setUTMParameters(obj)
	    AffiliateModule.updateAffiliateUserID(affiliateUserId)
	  } else {
	    const utmExpId = RequestHelper.getQueryVariable('utm_expid')

	    if (utmExpId) {
	      const utmReferrer = RequestHelper.getQueryVariable('utm_referrer')

	      const referrarUri = decodeURIComponent(decodeURIComponent(utmReferrer!))

	      obj.source = AffiliateModule.subDomain && (StringHelper.equalsIgnoringCase(AffiliateModule.subDomain, 'confused') || StringHelper.equalsIgnoringCase(AffiliateModule.subDomain, 'clearscore')) ? location.host : RequestHelper.getQueryVariableFromUrl(referrarUri, 'utm_source')
	      obj.medium = RequestHelper.getQueryVariableFromUrl(referrarUri, 'utm_medium') || RequestHelper.getQueryVariableFromUrl(referrarUri, 'medium')
	      obj.term = RequestHelper.getQueryVariableFromUrl(referrarUri, 'utm_term') || RequestHelper.getQueryVariableFromUrl(referrarUri, 'term')
	      obj.content = RequestHelper.getQueryVariableFromUrl(referrarUri, 'utm_content') || RequestHelper.getQueryVariableFromUrl(referrarUri, 'content')
	      obj.campaign = RequestHelper.getQueryVariableFromUrl(referrarUri, 'utm_campaign') || RequestHelper.getQueryVariableFromUrl(referrarUri, 'campaign')
	      obj.type = RequestHelper.getQueryVariableFromUrl(referrarUri, 'utm_type') || RequestHelper.getQueryVariableFromUrl(referrarUri, 'type')
	      obj.sid = RequestHelper.getQueryVariableFromUrl(referrarUri, 'utm_sid')
	      obj.mkwId = RequestHelper.getQueryVariableFromUrl(referrarUri, 'mkwid')
	      obj.agentPublicId = RequestHelper.getQueryVariableFromUrl(referrarUri, 'advisor')
	      obj.cid = obj.mkwId ? Number(RegExHelper.extract(obj.mkwId, /campaignid\|(\d+)/i)) : null
	      obj.referrerApplicationId = Number(RequestHelper.getQueryVariableFromUrl(referrarUri, 'rai'))

	      if (RequestHelper.getQueryVariableFromUrl(referrarUri, 'aid')) { affiliateUserId = RequestHelper.getQueryVariableFromUrl(referrarUri, 'aid') }

	      this.setUTMParameters(obj)
	      AffiliateModule.updateAffiliateUserID(affiliateUserId)
	    } else {
	      obj.source = AffiliateModule.subDomain && (StringHelper.equalsIgnoringCase(AffiliateModule.subDomain, 'confused') || StringHelper.equalsIgnoringCase(AffiliateModule.subDomain, 'clearscore')) ? location.host : RequestHelper.getQueryVariable('utm_source')
	      obj.medium = RequestHelper.getQueryVariable('utm_medium') || RequestHelper.getQueryVariable('medium')
	      obj.term = RequestHelper.getQueryVariable('utm_term') || RequestHelper.getQueryVariable('term')
	      obj.content = RequestHelper.getQueryVariable('utm_content') || RequestHelper.getQueryVariable('content')
	      obj.campaign = RequestHelper.getQueryVariable('utm_campaign') || RequestHelper.getQueryVariable('campaign')
	      obj.type = RequestHelper.getQueryVariable('utm_type') || RequestHelper.getQueryVariable('type')
	      obj.sid = RequestHelper.getQueryVariable('utm_sid')
	      obj.mkwId = RequestHelper.getQueryVariable('mkwid')
	      obj.agentPublicId = RequestHelper.getQueryVariable('advisor')
	      obj.cid = obj.mkwId ? Number(RegExHelper.extract(obj.mkwId, /campaignid\|(\d+)/i)) : null
	      obj.referrerApplicationId = Number(RequestHelper.getQueryVariable('rai'))

	      this.setUTMParameters(obj)
	      AffiliateModule.updateAffiliateUserID(affiliateUserId)
	    }
	  }
	}

	// #endregion

	// #region Click Id

	clickId = ''

	@Mutation
	private setClickId (val: string) {
		this.clickId = val
	}

	@Action
	updateClickId () {
		const clickId = RequestHelper.getQueryVariable('clickId')

		if (clickId) {
			this.setClickId(clickId)
		}
	}

	// #endregion

	// #region Quote Id

	quoteId: string | null = null;

	@Mutation
	private setQuoteId (val: string) {
		this.quoteId = val
	}

	@Action
	updateQuoteId () {
		const quoteId = RequestHelper.getQueryVariable('quoteId')

		if (quoteId) {
			this.setQuoteId(quoteId)
		}
	}

	// #endregion


	// #region Full App Success

	fullAppSuccess = false;

	@Mutation
	private setFullAppSuccess (val: boolean) {
	  this.fullAppSuccess = val
	}

	@Action
	updatFullAppSuccess (val: boolean) {
	  this.setFullAppSuccess(val)
	}

	// #endregion

	// #region Application ID

	applicationID: string | null = null;

	@Mutation
	private setApplicationID (val: string | null) {
	  this.applicationID = val
	}

	@Action
	updateApplicationID () {
	  let appId = RequestHelper.getQueryVariable('appid')

	  // attempt to parse bad id

	  if (!appId) {
	    const query = window.location.search.substring(1)

	    const vars = query.split('&')
	    for (let i = 0; i < vars.length; i++) {
	      const pair = vars[i].split('=')
	      if (pair[0].toLowerCase().includes('appid')) { appId = decodeURIComponent(pair[1]) }
	    }
	  }

	  this.setApplicationID(appId)
	}

	@Action
	updateApplicationIDWithValue (val: string) {
	  this.setApplicationID(val)
	}

	// #endregion

	// #region Application ID

	addresses: FullAddress[] = [];

	@Mutation
	private setFullAddresses (val: FullAddress[]) {
	  this.addresses = val
	}

	@Mutation
	private setFullAddress (val: FullAddress) {
	  const index = this.addresses.findIndex(a => a.index === val.index)
	  index > -1 ? this.addresses[index] = val : this.addresses.push(val)
	}

	@Action
	updateFullAddresses (val: FullAddress[]) {
	  this.setFullAddresses(val)
	}

	@Action
	updateFullAddress (val: FullAddress) {
	  this.setFullAddress(val)
	}

	// #endregion

	// #region Remote Address

	remoteAddress: string | null = null;

	@Mutation
	private setRemoteAddress (val: string | null) {
	  this.applicationID = val
	}

	@Action
	updateRemoteAddress (val: string | null) {
	  this.setRemoteAddress(val)
	}

	// #endregion

	// #region Device Context ID

	deviceContextId: string | null = null;

	@Mutation
	private setDeviceContextId (val: string | null) {
	  this.applicationID = val
	}

	@Action
	updateDeviceContextId (val: string | null) {
	  this.setDeviceContextId(val)
	}

	// #endregion

	// #region Dob

	dobDay: number | null = null;

	@Mutation
	private setDobDay (val: number | null) {
	  this.dobDay = val
	}

	@Action
	updateDobDay (val: number | null) {
	  this.setDobDay(val)
	}

	dobMonth: number | null = null;

	@Mutation
	private setDobMonth (val: number | null) {
	  this.dobMonth = val
	}

	@Action
	updateDobMonth (val: number | null) {
	  this.setDobMonth(val)
	}

	dobYear: number | null = null;

	@Mutation
	private setDobYear (val: number | null) {
	  this.dobYear = val
	}

	@Action
	updateDobYear (val: number | null) {
	  this.setDobYear(val)
	}

	// #endregion

	// #region members URL

	membersURL = '';

	@Mutation
	private setMembersURL (val: string) {
	  this.membersURL = val
	}

	@Action
	updateMembersURL (val: string) {
	  this.setMembersURL(val)
	}

	// #region members URL

	visitorId = '';

	@Mutation
	private setVisitorId (val: string) {
	  this.form.utmParameters.visitorId = val
	}

	@Action
	updateVisitorId (val: string) {
	  this.setVisitorId(val)
	}

	// #endregion

	// #region lock down

	lockDown = false;

	@Mutation
	private setLockDown (val: boolean) {
	  this.lockDown = val
	}

	@Action
	updateLockDown (val: boolean) {
	  this.setLockDown(val)
	}

	// #endregion

	// #region duplicate app

	isDuplicateApp = false;

	@Mutation
	private setIsDuplicateApp (val: boolean) {
	  this.isDuplicateApp = val
	}

	@Action
	updateIsDuplicateApp (val: boolean) {
	  this.setIsDuplicateApp(val)
	}

	// #endregion

	// #region submitting

	isSubmitting = false;

	@Mutation
	private setIsSubmitting (val: boolean) {
	  this.isSubmitting = val
	}

	@Action
	updateIsSubmitting (val: boolean) {
	  this.setIsSubmitting(val)
	}

	// #endregion

	editingAddressIndex = -1;

	@Mutation
	private setAddressIndex (val: number) {
	  this.editingAddressIndex = val
	}

	@Action
	updateAddressIndex (val: number) {
	  this.setAddressIndex(val)
	}

	editingEmploymentIndex = -1;

	@Mutation
	private setEmploymentIndex (val: number) {
	  this.editingEmploymentIndex = val
	}

	@Action
	updateEditingEmploymentIndex (val?: number) {
	  this.setEmploymentIndex(val ?? -1)
	}

	editingEmployment = {
	  yearsAtEmployer: 0,
	  monthsAtEmployer: 0,
	  employerName: '',
	  employerTown: '',
	  occupation: '',
	  employmentStatusLookup: {
	    id: -1
	  } as LookupValueDto
	} as EmploymentDto;

	@Mutation
	private setEmployment (val: EmploymentDto) {
	  this.editingEmployment = val
	}

	@Action
	updateEditingEmployment (val: EmploymentDto) {
	  this.setEmployment(val)
	}

	editingAddress = {
	  yearsAtAddress: 0,
	  monthsAtAddress: 0,
	  addressId: 0,
	  addressLevel: 0,
	  residentialStatusLookup: {
	    id: -1
	  } as LookupValueDto
	} as AddressDto;

	@Mutation
	private setAddress (val: AddressDto) {
	  this.editingAddress = val
	}

	@Action
	updateAddress (val: AddressDto) {
	  this.setAddress(val)
	}

	// #region Reset

	@Mutation
	private async resetStore () {
	  this.dobDay = null
	  this.dobMonth = null
	  this.dobYear = null
	  this.quoteId = null

	  this.form = {
	    title: {
	      id: 1,
	      name: 'Mr'
	    } as LookupValueDto,
	    vehicleType: {
	      id: VehicleType.Car // default to car
	    } as LookupValueDto,
	    maritalStatus: {
	      id: 24 // default to single
	    } as LookupValueDto,
	    drivingLicence: {
	      id: 35 // default to full
	    } as LookupValueDto,
	    firstName: '',
	    lastName: '',
	    email: '',
	    contactNumber: '',
	    loanAmount: DefaultValue.LoanAmount,
	    netMonthlyIncome: null,
	    dateOfBirth: null,
	    employments: [] as EmploymentDto[],
	    addresses: [] as AddressDto[]

	  } as ApplicationFormDto

	  this.appliedDate = null

	  this.addresses = []

	  this.editingAddressIndex = -1
	  this.editingAddress = {
	    yearsAtAddress: 0,
	    monthsAtAddress: 0,
	    addressLevel: 0,
	    residentialStatusLookup: {
	      id: -1
	    } as LookupValueDto
	  } as AddressDto

	  this.editingEmploymentIndex = -1
	  this.editingEmployment = {
	    yearsAtEmployer: 0,
	    monthsAtEmployer: 0,
	    occupation: '',
	    employerName: '',
	    employerTown: '',
	    employmentLevel: 0,
	    employmentStatusLookup: {
	      id: -1
	    } as LookupValueDto
	  } as EmploymentDto
	}

	@Action
	async reset () {
	  await this.resetStore()
	}

  // #endregion
}
